import { batch, createMemo, createSignal, For, type JSX, mergeProps, onCleanup, Show } from "solid-js";

import { render } from "solid-js/web";
import { items, itemsMap } from "~/data/items";
import { PageIndicator } from "~/js/components/pageIndicator";
import { game } from "~/js/game";
import { log } from "~/js/helper/weirdcaselogger";
import { Pages } from "~/js/pages";
import select from "~/js/select";
import { addStat, setStat } from "~/js/stats";
import { isContainerItem, isMasterItem, isSkinItem, isStickerItem } from "~/js/utils";
import { displayWinnings } from "~/js/winnings";
import { numToString, numToValuta, showPage } from "~/script";
import type { ContainerItem, Item, ItemName, SkinItem } from "~/types";
import { user } from "~/userStore";
export const itemRarity = {
	0: { color: "#fff", name: "consumer", display: "Consumer" },
	1: { color: "#99f", name: "industrial", display: "Industrial" },
	2: { color: "#33f", name: "milspec", display: "Milspec" },
	3: { color: "#84f", name: "restricted", display: "Restricted" },
	4: { color: "#f5f", name: "classified", display: "Classified" },
	5: { color: "#f22", name: "covert", display: "Covert" },
	6: { color: "#fd0", name: "exceedinglyrare", display: "Exceedingly Rare" },
	7: { color: "#fc7", name: "contraband", display: "Contraband" },
} as const;
const rarityMap = {
	consumer: 0,
	industrial: 1,
	milspec: 2,
	restricted: 3,
	classified: 4,
	covert: 5,
	exceedinglyrare: 6,
	contraband: 7,
} as const;
export const itemQualities = {
	1: "Battle-Scarred",
	2: "Well-Worn",
	3: "Field-Tested",
	4: "Minimal Wear",
	5: "Factory New",
} as const;
export const itemQualitiesShort = {
	1: "BS",
	2: "WW",
	3: "FT",
	4: "MW",
	5: "FN",
} as const;
export function getItem(e: ItemName | Item): Item {
	if (typeof e === "object") return e;
	const item = itemsMap.get(e);
	if (item) return item;
	else {
		throw new Error(`Item ${e} does not exist`);
	}
}

export function buyItems(itemName: Item, itemCount = 1, itemPrice: number) {
	if (user.money < itemPrice * itemCount) return false;

	user.money -= itemPrice * itemCount;
	giveItems(itemName, itemCount);

	game.emit("update");
	return true;
}

let sellLock = false;
export function sellItems(itemsP: ItemName[] | ItemName) {
	if (sellLock) return;
	sellLock = true;

	batch(() => {
		const itemsToSell = Array.isArray(itemsP) ? itemsP : [itemsP];

		// Build a map of item name -> quantity to sell
		const quantityMap = new Map<ItemName, number>();
		for (const itemName of itemsToSell) {
			quantityMap.set(itemName, (quantityMap.get(itemName) ?? 0) + 1);
		}

		// Accumulate totals for stats
		let totalMoneySold = 0;
		let totalSkinsSold = 0;
		let totalEarningsFromSkins = 0;
		let totalSoldGt10 = 0;
		let totalSoldGt50 = 0;
		let totalSoldGt250 = 0;
		let totalSoldR0 = 0;
		let totalSoldR1 = 0;
		let totalSoldR2 = 0;
		let totalSoldR3 = 0;
		let totalSoldR4 = 0;
		let totalSoldR5 = 0;
		let totalSoldR6 = 0;
		let totalSoldR7 = 0;

		// Single pass through inventory to remove items and accumulate stats
		let i = 0;
		let end = user.inventory.length - 1;
		while (i <= end) {
			const currentItem = user.inventory[i]!;
			const quantityToRemove = quantityMap.get(currentItem);

			if (quantityToRemove && quantityToRemove > 0) {
				quantityMap.set(currentItem, quantityToRemove - 1);
				// move last into current slot and pop last
				user.inventory[i] = user.inventory[end]!;
				end--;

				// Accumulate stats for this sold item
				let item: Item;
				try {
					item = getItem(currentItem);
				} catch {
					continue;
				}

				const totalValue = item.price || 0;
				totalMoneySold += totalValue;

				if (isSkinItem(item) || isStickerItem(item)) {
					totalSkinsSold += 1;
					totalEarningsFromSkins += totalValue;
					if (item.price > 1e3) totalSoldGt10 += 1;
					if (item.price > 5e3) totalSoldGt50 += 1;
					if (item.price > 25e3) totalSoldGt250 += 1;
					if (item.rarity === 0) totalSoldR0 += 1;
					else if (item.rarity === 1) totalSoldR1 += 1;
					else if (item.rarity === 2) totalSoldR2 += 1;
					else if (item.rarity === 3) totalSoldR3 += 1;
					else if (item.rarity === 4) totalSoldR4 += 1;
					else if (item.rarity === 5) totalSoldR5 += 1;
					else if (item.rarity === 6) totalSoldR6 += 1;
					else if (item.rarity === 7) totalSoldR7 += 1;
				}
				// do not increment i — we need to examine the newly swapped-in element
			} else {
				i++;
			}
		}
		user.inventory.length = end + 1;

		// Apply all stats at once
		user.money += totalMoneySold;
		if (totalSkinsSold > 0) {
			addStat("skin_sold", totalSkinsSold);
			addStat("earnings_from_skin_sales", totalEarningsFromSkins);
			addStat("earned_cash", totalEarningsFromSkins);
			if (totalSoldGt10 > 0) addStat("skin_sold_gt10", totalSoldGt10);
			if (totalSoldGt50 > 0) addStat("skin_sold_gt50", totalSoldGt50);
			if (totalSoldGt250 > 0) addStat("skin_sold_gt250", totalSoldGt250);
			if (totalSoldR0 > 0) addStat("skin_sold_r0", totalSoldR0);
			if (totalSoldR1 > 0) addStat("skin_sold_r1", totalSoldR1);
			if (totalSoldR2 > 0) addStat("skin_sold_r2", totalSoldR2);
			if (totalSoldR3 > 0) addStat("skin_sold_r3", totalSoldR3);
			if (totalSoldR4 > 0) addStat("skin_sold_r4", totalSoldR4);
			if (totalSoldR5 > 0) addStat("skin_sold_r5", totalSoldR5);
			if (totalSoldR6 > 0) addStat("skin_sold_r6", totalSoldR6);
			if (totalSoldR7 > 0) addStat("skin_sold_r7", totalSoldR7);
		}
	});

	sellLock = false;
	game.emit("update");
}

export function takeItems(item: Item, numberToTake: number) {
	return batch(() => {
		const numberOfItem = user.inventory.reduce((prev, cur) => {
			if (cur === item.name) return prev + 1;
			return prev;
		}, 0);
		if (numberOfItem >= numberToTake) {
			let i = 0;
			let removed = 0;
			const invlength = user.inventory.length;
			let end = invlength - 1;
			while (i < invlength && removed < numberToTake) {
				if (user.inventory[i] === item.name) {
					removed++;
					// move last into current slot and pop last
					user.inventory[i] = user.inventory[end]!;
					end--;
					// do not increment i — we need to examine the newly swapped-in element
				} else {
					i++;
				}
			}
			user.inventory.length = end + 1;
			return removed;
		} else {
			return numberOfItem;
		}
	});
}

export function getItemsValue(e: (ItemName | Item)[]) {
	return e.reduce((val, item) => val + getItem(item).price, 0);
}

export function getItemsWorth(targetWorth: number) {
	const itemArr: Item[] = [];
	const allSkinsUnderWorth = Object.values(items)
		.values()
		.filter((item) => isSkinItem(item))
		.filter((item) => item.price < targetWorth * 1.05 && (targetWorth < 1e3 || item.price > 500))
		.toArray();

	while (targetWorth > 10) {
		const randomItem = allSkinsUnderWorth[Math.floor(Math.random() * allSkinsUnderWorth.length)]!;
		if (randomItem.weight && randomItem.weight < Math.random()) continue;
		itemArr.push(randomItem);
		targetWorth -= itemArr.at(-1)!.price;
		addStat("total_winnings", itemArr.at(-1)!.price);
	}
	return itemArr;
}
export function giveItemsWorth(targetWorth: number) {
	showPage("winnings");
	const items = getItemsWorth(targetWorth);
	giveItems(items);
	displayWinnings({
		title: "Winnings",
		returnBtn: { text: "Go to inventory", page: "inventory" },
		items: items,
		sortBy: "value",
	});
}

export function removeItemsFromInventory(items: ItemName[]) {
	for (const item of items) {
		const index = user.inventory.indexOf(item);
		if (index === -1) {
			user.money -= getItem(item).price; // If item is not found, just deduct the price from money (shouldn't really happen)
			log("Tried to remove item that doesn't exist in inventory", { item, inventory: user.inventory, stack: new Error().stack });
		} else user.inventory.splice(index, 1);
	}
}
export function giveItems(items: Item, quantity: number): void;
export function giveItems(items: Item[]): void;
export function giveItems(items: Item[] | Item, quantity?: number) {
	const discoveredSet = new Set(user.discoveredItems);

	batch(() => {
		if (quantity && !Array.isArray(items)) {
			for (let i = 0; i < quantity; i++) {
				user.inventory.push(items.name);
			}
			discoveredSet.add(items.name);
			if ("short" in items) discoveredSet.add(items.short);
		} else if (Array.isArray(items)) {
			for (let i = 0; i < items.length; i++) {
				const item = items[i]!;
				user.inventory.push(item.name);

				discoveredSet.add(item.name);
				if ("short" in item) discoveredSet.add(item.short);
			}
			user.discoveredItems = Array.from(discoveredSet.values());
		}
	});

	setStat("items_discovered", user.discoveredItems.length);
	setStat(
		"skins_discovered",
		user.discoveredItems.filter((e) => {
			const t = getItem(e);
			return isMasterItem(t);
		}).length,
	);
	setStat(
		"stickers_discovered",
		user.discoveredItems.filter((e) => {
			const t = getItem(e);
			return isStickerItem(t);
		}).length,
	);
}

export function giveItem(itemName: ItemName, t = 1, showPopup = false) {
	giveItems(getItem(itemName), t);
	if (showPopup) {
		const popupEl = document.body.q(".popup-container", {
			onclick(e: MouseEvent) {
				if (e.target === popupEl) closePopup();
			},
		});
		const popupContent = popupEl.q(".popup-content");
		popupContent.q("button.popup-exit-button", {
			onclick() {
				closePopup();
			},
		});
		popupContent.q("h1", { text: t > 1 ? `You got ${t}:` : "You got!" });
		const itemElem = renderItem(getItem(itemName), { owned: true });
		itemElem.addEventListener("click", closePopup);
		popupContent.appendChild(itemElem);
		popupContent.q("button.button", {
			text: "Open Inventory",
			onclick() {
				showPage("inventory");
				closePopup();
			},
		});
		function closePopup() {
			popupEl.classList.add("popup-closed");
			setTimeout(() => {
				document.body.removeChild(popupEl);
			}, 250);
		}
	}
}

const containerCache = new Map<string, ContainerItem[]>();

export function getItemContainer(itemName: ItemName | Item): ContainerItem[] {
	const item = getItem(itemName);
	const targetName = isSkinItem(item) ? item.short : item.name;
	const cachedContainers = containerCache.get(targetName);

	if (cachedContainers) return cachedContainers;

	const containers = Iterator.from(Object.values(items))
		.filter(isContainerItem)
		.filter((item) => item.content.items.includes(targetName))
		.toArray();

	containerCache.set(targetName, containers);
	return containers;
}

function* chain<T, U>(iterA: IteratorObject<T>, iterB: IteratorObject<U>) {
	yield* iterA;
	yield* iterB;
}
export function getInventoryValue(includeContainers = false, includeFavorites = false) {
	const i = includeFavorites ? Iterator.from(user.favinv) : chain(Iterator.from(user.inventory), Iterator.from(user.favinv));
	return i
		.map((item) => getItem(item))
		.filter((item) => !isContainerItem(item) || includeContainers)
		.reduce((prev, curr) => prev + curr.price, 0);
}
function addToFavInv(itemName: ItemName) {
	user.favinv.push(itemName);
	removeItemsFromInventory([itemName]);
	game.emit("update");
}
function removeFromFavInv(itemName: ItemName) {
	giveItem(itemName);
	user.favinv.splice(user.favinv.indexOf(itemName), 1);
	game.emit("update");
}
export function getRandomRarity(targetRarity: SkinItem["rarity"]) {
	const filteredSkinItems = Object.values(items).filter((t) => isSkinItem(t) && t.rarity === targetRarity);
	return filteredSkinItems[Math.floor(Math.random() * filteredSkinItems.length)]!.name;
}

interface ItemOptions {
	clickable?: boolean;
	owned?: boolean;
	favorite?: boolean;
	noOptionalCompact?: boolean;
	amount?: number;
	pricing?: boolean;
	buy?: boolean;
	buyAmount?: number;
	buyPrice?: number;
	preDiscountPrice?: number;
	buyDisabled?: boolean;
	sell?: boolean;
	class?: string | undefined;
	onclick?: (item: Item) => void;
}

export function renderItem(item: Item, itemOptions?: ItemOptions) {
	const container = q(".item-container");
	container.classList.toggle("no-optional-compact", itemOptions?.noOptionalCompact || false);
	const itemEl = container.q(".item", {
		style: { borderColor: itemRarity[item.rarity].color },
	});
	if (!itemOptions || itemOptions.clickable !== false) {
		itemEl.onclick =
			itemOptions?.onclick && "onclick" in itemOptions
				? () => itemOptions.onclick!(item)
				: () => {
						itemOptions?.owned ? showPage(`inspect$${item.name}&owned`) : showPage(`inspect$${item.name}`);
					};
	}
	const itemImageEl = itemEl.q(".item-image", {
		style: { backgroundImage: `url("${item.src}")` },
	});
	if ("quality" in item && item.quality) itemImageEl.q(".item-quality", itemQualitiesShort[item.quality]);
	if ("stattrak" in item) itemEl.q(".item-stattrak");
	if ("souvenir" in item) {
		itemImageEl.style.boxShadow = "1px 0 16px #fc04 inset";
		itemEl.q(".item-souvenir", "Souvenir");
	}
	if (itemOptions?.amount && itemOptions.amount > 1) itemImageEl.q(".item-amount-text", `x${itemOptions.amount}`);
	if (itemOptions?.pricing) itemEl.q(".item-price", numToValuta(item.price, 2));
	const itemTextEl = itemEl.q(".item-text");
	itemTextEl.q(
		".item-type",
		isSkinItem(item) ? item.weapon : isStickerItem(item) ? item.stickerType : item.type[0]!.toUpperCase() + item.type.substring(1),
	);
	itemTextEl.q(".item-name", isSkinItem(item) || isMasterItem(item) ? item.skin : isStickerItem(item) ? item.sticker : item.name);
	if (itemOptions?.sell) {
		const sellAmount = itemOptions.amount ?? 1;
		if (sellAmount > 1) {
			container.q("button.button.item-sell", `Sell all for ${numToValuta(item.price * sellAmount, 2)}`, {
				onclick: () => sellItems(Array(sellAmount).fill(item.name)),
			});
		} else {
			container.q("button.button.item-sell", `Sell for ${numToValuta(item.price, 2)}`, {
				onclick: () => sellItems(item.name),
			});
		}
	}
	if (itemOptions?.buy && typeof itemOptions.buyPrice === "number") {
		const buyAmount = itemOptions.buyAmount ?? 1;
		const buyPrice = Math.floor(itemOptions.buyPrice);
		const totalPrice = buyPrice * buyAmount;
		const preDiscount = typeof itemOptions.preDiscountPrice === "number" ? Math.floor(itemOptions.preDiscountPrice) * buyAmount : null;
		container.q("button.button.item-buy", {
			html: `${buyAmount > 1 ? `Buy ${buyAmount}x` : "Buy"} for ${numToValuta(totalPrice, 2)}${preDiscount ? ` <span style="text-decoration:line-through">${numToValuta(preDiscount, 2)}</span>` : ""}`,
			onclick: () => !itemOptions.buyDisabled && buyItems(item, buyAmount, buyPrice),
			disabled: user.money < totalPrice,
		});
	}
	if (itemOptions?.owned)
		container.q(".item-set-fav", {
			onclick: () => addToFavInv(item.name),
			attr: {
				tip: "Add to favorites",
				tipPosition: "center",
			},
		});
	if (itemOptions?.owned && itemOptions.favorite)
		container.q(".item-set-fav.fav", {
			onclick: () => removeFromFavInv(item.name),
			attr: {
				tip: "Remove from favorites",
				tipPosition: "center",
			},
		});
	const a = itemEl.q(".item-3d-shine-effect");
	container.onmouseleave = () => {
		itemEl.classList.add("transform-ended");
		itemEl.style.transform = "rotateY(0deg) rotateX(0deg) scale(1)";
		a.style.opacity = "0";
	};
	container.addEventListener("mousemove", (e) => {
		if (window.graphicsLevel > 1) {
			const t = itemEl.getBoundingClientRect();
			if ((e.y - t.y) / t.height <= 1) {
				itemEl.classList.remove("transform-ended");
				const n = (e.x - t.x) / t.width - 0.5;
				const i = (e.y - t.y) / t.height - 0.5;
				itemEl.style.transform = `rotateY(${n * 20}deg) rotateX(${-i * 20}deg) scale(1.05)`;
				a.style.opacity = "1";
				a.style.top = `${Math.min(0, -i * 100 - 50)}%`;
				a.style.left = `${-n * 100 - 50}%`;
			} else {
				itemEl.classList.add("transform-ended");
				itemEl.style.transform = "rotateY(0deg) rotateX(0deg) scale(1)";
				a.style.opacity = "0";
			}
		}
	});
	return container;
}
// TODO: break up this giant component
export function ItemCard(
	props: { item: Item; ref?: HTMLDivElement | ((el: HTMLDivElement) => void); children?: JSX.Element } & ItemOptions,
) {
	const [transformStyle, setTransformStyle] = createSignal("rotateY(0deg) rotateX(0deg) scale(1)");
	const [shineOpacity, setShineOpacity] = createSignal("0");
	const [shineTop, setShineTop] = createSignal("0%");
	const [shineLeft, setShineLeft] = createSignal("0%");
	const [transformEnded, setTransformEnded] = createSignal(true);

	let itemRef: HTMLDivElement | undefined;

	function resetTransform() {
		setTransformEnded(true);
		setTransformStyle("rotateY(0deg) rotateX(0deg) scale(1)");
		setShineOpacity("0");
	}

	function handleMouseLeave() {
		resetTransform();
	}

	function handleMouseMove(e: MouseEvent) {
		if (graphicsLevel <= 1 || !itemRef) return; // feature disabled on low graphics
		const rect = itemRef.getBoundingClientRect();
		const relY = (e.y - rect.y) / rect.height;
		if (relY > 1 || relY < 0) {
			resetTransform();
			return;
		}
		setTransformEnded(false);
		const n = (e.x - rect.x) / rect.width - 0.5; // X axis (-0.5 to 0.5)
		const i = (e.y - rect.y) / rect.height - 0.5; // Y axis (-0.5 to 0.5)
		setTransformStyle(`rotateY(${n * 20}deg) rotateX(${-i * 20}deg) scale(1.05)`);
		setShineOpacity("1");
		// These replicate the calculations from renderItem()
		setShineTop(`${Math.min(0, -i * 100 - 50)}%`);
		setShineLeft(`${-n * 100 - 50}%`);
	}

	return (
		<div
			class={props.class ? `item-container ${props.class}` : "item-container"}
			classList={{ "no-optional-compact": !props.noOptionalCompact }}
			on:mouseleave={handleMouseLeave}
			on:mousemove={handleMouseMove}
			ref={props.ref}
		>
			<div
				class="item"
				classList={{ "transform-ended": transformEnded() }}
				onclick={
					props.clickable !== false
						? props.onclick
							? () => props.onclick!(props.item)
							: props.owned
								? () => showPage(`inspect$${props.item.name}&owned`)
								: () => showPage(`inspect$${props.item.name}`)
						: undefined
				}
				ref={itemRef}
				style={{
					"border-color": itemRarity[props.item.rarity].color,
					transform: transformStyle(),
				}}
			>
				<div
					class="item-image"
					style={{
						"background-image": `url(${props.item.src})`,
						"box-shadow": "souvenir" in props.item && props.item.souvenir ? "1px 0 16px #fc04 inset" : undefined,
					}}
				>
					{"quality" in props.item && props.item.quality && <div class="item-quality">{itemQualitiesShort[props.item.quality]}</div>}
					<Show when={props.amount && props.amount > 1}>
						<div class="item-amount-text">x{props.amount}</div>
					</Show>
					<Show when={props.pricing}>
						<div class="item-pricing">{numToValuta(props.item.price, 2)}</div>
					</Show>
				</div>
				{"stattrak" in props.item && props.item.stattrak && <div class="item-stattrak"></div>}
				{"souvenir" in props.item && props.item.souvenir && <div class="item-souvenir">Souvenir</div>}
				<div class="item-text">
					<div class="item-type">
						{isSkinItem(props.item)
							? props.item.weapon
							: isStickerItem(props.item)
								? props.item.stickerType
								: props.item.type[0]!.toUpperCase() + props.item.type.substring(1)}
					</div>
					<div class="item-name">
						{isSkinItem(props.item) || isMasterItem(props.item)
							? props.item.skin
							: isStickerItem(props.item)
								? props.item.sticker
								: props.item.name}
					</div>
				</div>
				<div
					class="item-3d-shine-effect"
					style={{
						opacity: shineOpacity(),
						top: shineTop(),
						left: shineLeft(),
					}}
				/>
			</div>
			{props.sell &&
				((props.amount ?? 0) > 1 ? (
					<button
						class="button item-sell"
						on:click={() => {
							sellItems(Array(props.amount ?? 1).fill(props.item.name));
						}}
						type="button"
					>
						Sell all for {numToValuta(props.item.price * (props.amount ?? 1), 2)}
					</button>
				) : (
					<button
						class="button item-sell"
						on:click={() => {
							sellItems(props.item.name);
						}}
						type="button"
					>
						Sell for {numToValuta(props.item.price, 2)}
					</button>
				))}
			{props.buy && props.buyPrice && (
				<button
					class="button item-buy"
					disabled={user.money < Math.floor(props.buyPrice) * (props.buyAmount || 1)}
					onclick={() => {
						console.log("buy clicked");
						if (!props.buyDisabled && props.buyPrice) buyItems(props.item, props.buyAmount, Math.floor(props.buyPrice));
					}}
					type="button"
				>
					{props.buyAmount && props.buyAmount > 1 ? `Buy ${props.buyAmount}x ` : "Buy "}
					for {numToValuta(Math.floor(props.buyPrice) * (props.buyAmount || 1), 2)}
					{props.preDiscountPrice ? (
						<>
							{" "}
							<span style={{ "text-decoration": "line-through" }}>
								{numToValuta(Math.floor(props.preDiscountPrice) * (props.buyAmount || 1), 2)}
							</span>
						</>
					) : undefined}
				</button>
			)}

			<Show when={props.owned}>
				<div class="item-set-fav" on:click={() => addToFavInv(props.item.name)} data-tip="Add to favorites" data-tipPosition="center" />
			</Show>
			<Show when={props.owned && props.favorite}>
				<div
					class="item-set-fav fav"
					on:click={() => removeFromFavInv(props.item.name)}
					data-tip="Remove from favorites"
					data-tipPosition="center"
				/>
			</Show>
			{props.children}
		</div>
	);
}

const pageRoot = Pages.createPage("selectItems");

// ---------------- Solid Select Items (new implementation) ----------------

// Utility replicating legacy per-page calculation
function getItemsPerPage() {
	// Matches legacy formula: half width (two columns) / item width * (height - header stuff)/item height
	const pagesRoot = select("#pages");
	if (!pagesRoot) return 20; // fallback
	const perRow = Math.max(1, Math.floor(pagesRoot.offsetWidth / 2 / 118));
	const rows = Math.max(1, Math.floor((pagesRoot.offsetHeight - 246) / 146));
	return perRow * rows;
}

interface SelectItemsProps {
	minBet?: number | null;
	maxBet?: number | null;
	maxItems?: number;
	minItems?: number;
	isTradeUp?: boolean;
	isBetting?: boolean;
	isChipsConversion?: boolean;
	onselect: (items: Item[] | false) => void;
	cancel: () => void;
}

export function SelectItems(props: SelectItemsProps) {
	const mergedProps = mergeProps(
		{ minBet: null, maxBet: null, maxItems: 1e3, minItems: 1, isTradeUp: false, isBetting: true, isChipsConversion: false },
		props,
	);
	// Page indices
	const [ownedPage, setOwnedPage] = createSignal(0);
	const [selectedPage, setSelectedPage] = createSignal(0);
	// Filter mode: 'none' | 'auto' | 'tier:<name>'
	const [filterMode, setFilterMode] = createSignal<string>("auto");
	// Source data arrays store item name strings
	const [ownedItems, setOwnedItems] = createSignal<ItemName[]>([]);
	const [selectedItems, setSelectedItems] = createSignal<ItemName[]>([]);
	const [ownedValue, setOwnedValue] = createSignal(0);
	const [selectedValue, setSelectedValue] = createSignal(0);
	const [itemsPerPage, setItemsPerPage] = createSignal(getItemsPerPage());

	// Initialize owned items list similar to legacy O
	function initOwned() {
		let q = 0;
		const inv = Array.from(user.inventory)
			.filter((e) => {
				const it = getItem(e);
				if (it.type === "skin" || it.type === "sticker") {
					q += it.price;
					return true;
				}
				return false;
			})
			.sort((a, b) => {
				// Same two-pass sort: first by name descending, then by price descending
				const an = getItem(a).name;
				const bn = getItem(b).name;
				if (an < bn) return 1;
				if (an > bn) return -1;
				return 0;
			})
			.sort((a, b) => getItem(b).price - getItem(a).price);
		setOwnedItems(inv);
		setOwnedValue(q);
	}
	initOwned();

	// Resize handling for dynamic pagination
	function handleResize() {
		setItemsPerPage(getItemsPerPage());
	}
	window.addEventListener("resize", handleResize);
	onCleanup(() => window.removeEventListener("resize", handleResize));

	// Filtering of owned items, replicating legacy logic
	const filteredOwned = createMemo(() => {
		let list = ownedItems().filter((e) => {
			const t = getItem(e);
			return t.type === "skin" || t.type === "sticker";
		});
		const mode = filterMode();
		if (mode.startsWith("tier:")) {
			const rarityName = mode.substring(5) as keyof typeof rarityMap;
			list = list.filter((t) => getItem(t).rarity === rarityMap[rarityName]);
		} else if (mode === "auto") {
			if (selectedItems()[0]) {
				if (mergedProps.isTradeUp) {
					const firstItem = getItem(selectedItems()[0]!).rarity;
					list = list.filter((t) => getItem(t).rarity === firstItem);
				}
			}
			if (mergedProps.maxBet) list = list.filter((e) => getItem(e).price + selectedValue() < mergedProps.maxBet!);
			if (mergedProps.isTradeUp) list = list.filter((e) => ![5, 6, 7].includes(getItem(e).rarity));
		}
		// Sort by price descending like legacy inside renderSelection
		return list.slice().sort((a, b) => getItem(b).price - getItem(a).price);
	});

	const sortedSelected = createMemo(() =>
		selectedItems()
			.slice()
			.sort((a, b) => getItem(b).price - getItem(a).price),
	);

	// Validation + confirm text
	const tradeUpInvalid = createMemo(() => {
		if (!mergedProps.isTradeUp) return false as false | "NST" | "NUT";
		let mismatch: false | "NST" | "NUT" = false;
		let firstRarity: number | false = false;
		for (const n of selectedItems()) {
			if (mismatch) break;
			const r = getItem(n).rarity;
			if (firstRarity === false) firstRarity = r;
			if (firstRarity !== r) mismatch = "NST";
			if (r > 4) mismatch = "NUT";
		}
		return mismatch;
	});

	const confirmDisabled = createMemo(() => {
		const len = selectedItems().length;
		const T = selectedValue();
		return (
			len === 0 ||
			T < (mergedProps.minBet || 0) ||
			(!!mergedProps.maxBet && T > mergedProps.maxBet) ||
			len > mergedProps.maxItems ||
			len < mergedProps.minItems ||
			Boolean(tradeUpInvalid())
		);
	});

	const confirmText = createMemo(() => {
		const len = selectedItems().length;
		const T = selectedValue();
		if (len === 0) return "Select at least one item";
		if (tradeUpInvalid() === "NST") return "All selected items must be of the same tier";
		if (tradeUpInvalid() === "NUT") return "Some selected items cannot be used";
		if (len > mergedProps.maxItems) return `You cannot ${mergedProps.isBetting ? "bet" : "select"} over ${mergedProps.maxItems} items`;
		if (len < mergedProps.minItems) return `You have to ${mergedProps.isBetting ? "bet" : "select"} atleast ${mergedProps.minItems} items`;
		if (mergedProps.minBet && T < mergedProps.minBet)
			return `You must ${mergedProps.isBetting ? "bet" : "select"} a minimum of ${numToValuta(mergedProps.minBet, 2)}`;
		if (mergedProps.maxBet && T > mergedProps.maxBet)
			return `You must ${mergedProps.isBetting ? "bet" : "select"} a maximum of ${numToValuta(mergedProps.maxBet, 2)}`;
		return "Confirm Selection";
	});

	// Actions
	function addItem(name: ItemName) {
		setSelectedItems((prev) => [...prev, name]);
		setOwnedItems((prev) => prev.filter((i) => i !== name));
		setOwnedValue((v) => v - getItem(name).price);
		setSelectedValue((v) => v + getItem(name).price);
	}
	function removeItem(idx: number) {
		const name = sortedSelected()[idx];
		if (!name) return;
		setOwnedItems((prev) => [...prev, name]);
		setOwnedValue((v) => v + getItem(name).price);
		setSelectedValue((v) => v - getItem(name).price);
		setSelectedItems((prev) => {
			const copy = prev.slice();
			const remIdx = copy.indexOf(name);
			if (remIdx !== -1) copy.splice(remIdx, 1);
			return copy;
		});
	}
	function clearSelection() {
		setSelectedItems([]);
		setSelectedValue(0);
		initOwned();
		setOwnedPage(0);
		setSelectedPage(0);
	}
	function allIn() {
		if (!(mergedProps.isChipsConversion || mergedProps.isBetting)) return;
		const newSelected: ItemName[] = [];
		let total = 0;
		for (const itemName of user.inventory) {
			const item = getItem(itemName);
			if (item.type === "skin" || item.type === "sticker") {
				newSelected.push(itemName);
				total += item.price;
			}
		}
		setSelectedItems(newSelected);
		setSelectedValue(total);
		setOwnedItems([]);
		setOwnedValue(0);
		setOwnedPage(0);
		setSelectedPage(0);
	}
	function autoBet() {
		if (!mergedProps.isBetting) return;
		let T = 0;
		const sel: ItemName[] = [];
		const rest: ItemName[] = [];
		let q = 0;
		Array.from(user.inventory)
			.sort((a, b) => getItem(b).price - getItem(a).price)
			.forEach((e) => {
				const t = getItem(e);
				if (
					(t.type === "skin" || t.type === "sticker") &&
					(!mergedProps.maxBet || T + t.price < mergedProps.maxBet) &&
					sel.length < mergedProps.maxItems
				) {
					sel.push(e);
					T += t.price;
				} else if (t.type === "skin" || t.type === "sticker") {
					rest.push(e);
					q += t.price;
				}
			});
		setSelectedItems(sel);
		setSelectedValue(T);
		setOwnedItems(rest);
		setOwnedValue(q);
		setOwnedPage(0);
		setSelectedPage(0);
	}

	function confirm() {
		if (confirmDisabled()) return;
		mergedProps.onselect(selectedItems().map((e) => getItem(e)));
	}
	function cancel() {
		mergedProps.onselect(false);
		mergedProps.cancel();
	}

	// Derived counts
	const ownedPageCount = createMemo(() => Math.max(1, Math.ceil(filteredOwned().length / itemsPerPage())));
	const selectedPageCount = createMemo(() => Math.max(1, Math.ceil(sortedSelected().length / itemsPerPage())));

	// Clamp pages reactively
	createMemo(() => {
		if (ownedPage() >= ownedPageCount()) setOwnedPage(Math.max(0, ownedPageCount() - 1));
		if (selectedPage() >= selectedPageCount()) setSelectedPage(Math.max(0, selectedPageCount() - 1));
	});

	const pagedOwned = createMemo(() => filteredOwned().slice(ownedPage() * itemsPerPage(), (ownedPage() + 1) * itemsPerPage()));
	const pagedSelected = createMemo(() => sortedSelected().slice(selectedPage() * itemsPerPage(), (selectedPage() + 1) * itemsPerPage()));

	const selectInfo = createMemo(() => {
		if (mergedProps.minBet) {
			return mergedProps.maxBet
				? `Make a bet between ${numToValuta(mergedProps.minBet, 2)} – ${numToValuta(mergedProps.maxBet, 2)}`
				: `Make a bet over ${numToValuta(mergedProps.minBet, 2)}`;
		}
		return mergedProps.isTradeUp
			? "Select items to trade up. They should be the same tier. For now you can mix stattrak/non-stattrak skins."
			: `Select items${mergedProps.isBetting ? " for your bet." : mergedProps.isChipsConversion ? " to convert to chips." : "."}`;
	});

	// Filter dropdown options
	const rarityButtons = Object.values(itemRarity).slice(0, 7);

	return (
		<div class="solid-select-items">
			<div class="center">
				<h1 class="select-items-header">Select Items</h1>
				<p class="select-items-info">{selectInfo()}</p>
				<div class="select-items-controls">
					<div class="select-items-controls-left">
						<div class="css-dropdown">
							<button class="button" type="button">
								{filterMode() === "none" ? "Filter: All" : filterMode()}
							</button>
							<div class="css-dropdown-contents">
								<button class="button" onClick={() => setFilterMode("none")} type="button">
									All
								</button>
								<button class="button" onClick={() => setFilterMode("auto")} type="button">
									Auto
								</button>
								<For each={rarityButtons}>
									{(r) => (
										<button class="button" onClick={() => setFilterMode(`tier:${r.name}`)} style={{ color: r.color }} type="button">
											Only {r.display}
										</button>
									)}
								</For>
							</div>
						</div>

						<PageIndicator currentPage={ownedPage} setPage={setOwnedPage} totalPages={ownedPageCount} />
					</div>
					<div class="select-items-controls-right">
						<button class="button" onClick={clearSelection} type="button">
							Clear
						</button>
						<PageIndicator currentPage={selectedPage} setPage={setSelectedPage} totalPages={selectedPageCount} />
						<Show when={mergedProps.isChipsConversion || mergedProps.isBetting}>
							<button class="button" onClick={allIn} type="button">
								All In
							</button>
						</Show>
						<Show when={mergedProps.isBetting}>
							<button class="button" onClick={autoBet} type="button">
								Auto Bet
							</button>
						</Show>
					</div>
				</div>
			</div>
			<div class="flex">
				<div class="compact" id="select-owned-items">
					<div class="select-cat">Your Items ({numToValuta(ownedValue(), 2)})</div>
					<For each={pagedOwned()}>{(name) => <ItemCard item={getItem(name)} onclick={() => addItem(name)} pricing />}</For>
				</div>
				<div class="compact" id="select-selected-items">
					<div class="select-cat">
						Selected Items ({numToValuta(selectedValue(), 2)}
						{mergedProps.isChipsConversion ? ` - ${numToString(Math.round((selectedValue() / 100) * 8))} Chips` : ""})
					</div>
					<For each={pagedSelected()}>{(name, i) => <ItemCard item={getItem(name)} onclick={() => removeItem(i())} pricing />}</For>
				</div>
			</div>
			<div class="center">
				<div>
					<button class="button" onClick={cancel} type="button">
						Cancel
					</button>
					<button class="button" disabled={confirmDisabled()} onClick={confirm} type="button">
						{confirmText()}
					</button>
				</div>
			</div>
		</div>
	);
}

let solidDispose: (() => void) | null = null;

export function showSelectItems(
	minBet: number | null = null,
	maxBet: number | null = null,
	maxItems = 1e3,
	minItems = 1,
	isTradeUp = false,
	isBetting = true,
	isChipsConversion = false,
): Promise<Item[] | false> {
	return new Promise((resolve) => {
		showPage("selectItems", true);
		// Create / reuse solid root container
		const root = document.createElement("div");
		pageRoot.innerHTML = "";

		if (solidDispose) solidDispose();
		solidDispose = render(
			() => (
				<SelectItems
					cancel={() => {
						if (solidDispose) solidDispose();
						solidDispose = null;
						resolve(false);
					}}
					isBetting={isBetting}
					isChipsConversion={isChipsConversion}
					isTradeUp={isTradeUp}
					maxBet={maxBet}
					maxItems={maxItems}
					minBet={minBet}
					minItems={minItems}
					onselect={(items) => {
						if (solidDispose) solidDispose();
						solidDispose = null;
						resolve(items);
					}}
				/>
			),
			root,
		);
		pageRoot.appendChild(root.firstChild!);
	});
}
